import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import {
  getAdminInGameStats,
  getAdminStatCosts,
  getAdminWeeklyRules,
  updateAdminInGameStats,
  updateAdminStatCosts,
  updateAdminWeeklyRule,
  type InGameStatEntry,
  type InGameStatPatch,
  type StatCatalogStat,
  type StatCostsPatch,
  type WeeklyRulesEntry,
} from '../../lib/api/admin';
import { fmtDateTime } from '../../lib/format';
import { isoToLocalInput, localInputToIso } from '../../lib/datetimeLocal';

/**
 * Catalog tab — three editors. The two stats editors sit together at the top
 * because they describe the same domain (persistent shop slots + in-game
 * level-up picks); weekly rules is a separate legal/publication concern.
 *
 *   1. Stat costs grid — one row per persistent shop stat, one input per level.
 *      Stats with a higher maxLevel get more columns (cell layout adapts). PUT
 *      replaces the whole grid atomically; we send only changed stats but the
 *      dirty check is per-row, not per-cell.
 *   2. In-game level-up picks — per-row inline editor for the 15
 *      `type='in_game'` rows. Editable fields: description, sortOrder, theme,
 *      iconAsset (dropdown of valid puMenu/ basenames), enabled. statId,
 *      name, maxLevel, and relatedStatId render read-only — the catalog
 *      shape for those is migration-controlled.
 *   3. Weekly rules — per-week card with title / version / effectiveFrom /
 *      published. Each week is saved independently so a typo in one week
 *      doesn't roll back the others. The rules body itself lives in web-side
 *      React (no body column), so this surface only manages the publication
 *      bits legal cares about.
 */
export function CatalogTab() {
  return (
    <div className="flex flex-col gap-8">
      <StatCostsEditor />
      <InGameStatsEditor />
      <WeeklyRulesEditor />
    </div>
  );
}

// ─── Stat costs ──────────────────────────────────────────────────────

interface PersistentDraft {
  description: string;
  enabled: boolean;
  // Per-level cost as the raw input string — preserved so an admin can
  // clear a cell mid-edit without it snapping to 0.
  costs: Record<number, string>;
}

type Message = { kind: 'error' | 'success'; text: string };

function StatCostsEditor() {
  const [stats, setStats] = useState<StatCatalogStat[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, PersistentDraft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // One slot, not two — error and success are mutually exclusive.
  const [message, setMessage] = useState<Message | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAdminStatCosts({ signal: controller.signal })
      .then((d) => {
        setStats(d);
        setDrafts(buildDrafts(d));
        setMessage(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setMessage({
          kind: 'error',
          text:
            err instanceof ApiError
              ? `Couldn't load stat costs (${err.status}).`
              : "Couldn't load stat costs.",
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const maxLevelCol = useMemo(() => Math.max(1, ...(stats?.map((s) => s.maxLevel) ?? [1])), [stats]);

  const dirty = useMemo(() => {
    if (!stats) return false;
    for (const s of stats) {
      const draft = drafts[s.statId];
      if (!draft) continue;
      if (persistentRowDiffers(s, draft)) return true;
    }
    return false;
  }, [stats, drafts]);

  const setCell = (statId: string, level: number, value: string): void => {
    setDrafts((cur) => ({
      ...cur,
      [statId]: {
        ...cur[statId]!,
        costs: { ...cur[statId]!.costs, [level]: value },
      },
    }));
  };

  const patch = (statId: string, p: Partial<Omit<PersistentDraft, 'costs'>>): void => {
    setDrafts((cur) => ({ ...cur, [statId]: { ...cur[statId]!, ...p } }));
  };

  const reset = (): void => {
    if (!stats) return;
    setDrafts(buildDrafts(stats));
    setMessage(null);
  };

  const save = async (): Promise<void> => {
    if (!stats) return;
    const changed: StatCostsPatch[] = [];
    for (const s of stats) {
      const draft = drafts[s.statId];
      if (!draft) continue;
      if (!persistentRowDiffers(s, draft)) continue;

      // Per-row validation — surface the first error and stop so the admin's
      // other edits aren't dropped.
      if (!draft.description.trim()) {
        setMessage({ kind: 'error', text: `"${s.name}" description is required.` });
        return;
      }

      // Costs block. Include it in the patch only if any cost cell actually
      // moved — keeps the payload minimal and avoids redundant upserts.
      let costsDirty = false;
      const costs: Array<{ level: number; coinCost: number }> = [];
      for (const c of s.costs) {
        const raw = draft.costs[c.level] ?? '';
        const n = Number(raw);
        if (!Number.isInteger(n) || n < 0 || raw.trim() === '') {
          setMessage({ kind: 'error', text: `Stat "${s.name}" level ${c.level} must be a non-negative integer.` });
          return;
        }
        if (n !== c.coinCost) costsDirty = true;
        costs.push({ level: c.level, coinCost: n });
      }

      const out: StatCostsPatch = { statId: s.statId };
      if (costsDirty) out.costs = costs;
      if (draft.description !== s.description) out.description = draft.description.trim();
      if (draft.enabled !== s.enabled) out.enabled = draft.enabled;
      changed.push(out);
    }
    if (changed.length === 0) return;

    setSaving(true);
    setMessage(null);
    try {
      const updated = await updateAdminStatCosts({ stats: changed });
      setStats(updated);
      setDrafts(buildDrafts(updated));
      setMessage({ kind: 'success', text: `Saved ${changed.length} stat${changed.length === 1 ? '' : 's'}.` });
    } catch (err) {
      setMessage({
        kind: 'error',
        text: err instanceof ApiError ? err.message : "Couldn't save stat costs.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-white/15 bg-black/20 p-5">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="font-display text-xl uppercase tracking-wide text-white">
            Persistent shop slots
          </h2>
          <p className="text-sm text-white/55">
            Coin cost per level plus per-stat description and enabled toggle. Tune costs for
            run-economy balance; flip <code>enabled</code> off to pull a stat out of the lobby
            shop without dropping its row from the catalog.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={reset}
            disabled={!dirty || saving}
            className="rounded-full border border-white/25 px-4 py-2 text-xs uppercase tracking-wide text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || saving}
            className="rounded-full bg-[#00ff8b] px-4 py-2 text-xs font-medium uppercase tracking-wide text-[#10231a] transition hover:bg-[#5bffb4] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </header>

      {message && (
        <p className={`mt-3 text-sm ${message.kind === 'error' ? 'text-[#ff8da6]' : 'text-[#5bffb4]'}`}>
          {message.text}
        </p>
      )}

      <div className={`mt-4 overflow-x-auto ${loading ? 'opacity-60' : 'opacity-100'}`}>
        {loading && !stats ? (
          <p className="text-sm text-white/55">Loading catalog…</p>
        ) : !stats || stats.length === 0 ? (
          <p className="text-sm text-white/55">No stats configured.</p>
        ) : (
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/55">
                <Th>Stat</Th>
                <Th>Description</Th>
                <Th>Max</Th>
                {Array.from({ length: maxLevelCol }).map((_, i) => (
                  <Th key={i}>Lv {i + 1}</Th>
                ))}
                <Th>On</Th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => {
                const d = drafts[s.statId];
                if (!d) return null;
                return (
                  <tr key={s.statId} className="border-b border-white/5 align-top last:border-0 text-white/85">
                    <td className="px-3 py-3">
                      <div className="font-medium text-white">{s.name}</div>
                      <div className="text-xs text-white/45 [overflow-wrap:anywhere]">{s.statId}</div>
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={d.description}
                        onChange={(e) => patch(s.statId, { description: e.target.value })}
                        maxLength={255}
                        className="w-full min-w-[220px] rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-white focus:border-white/40 focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-3 text-white/70">{s.maxLevel}</td>
                    {Array.from({ length: maxLevelCol }).map((_, i) => {
                      const level = i + 1;
                      const has = level <= s.maxLevel;
                      const value = d.costs[level] ?? '';
                      return (
                        <td key={level} className="px-2 py-2">
                          {has ? (
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              value={value}
                              onChange={(e) => setCell(s.statId, level, e.target.value)}
                              className="w-20 rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-white focus:border-white/40 focus:outline-none"
                            />
                          ) : (
                            <span className="text-white/30">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-2 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={d.enabled}
                        onChange={(e) => patch(s.statId, { enabled: e.target.checked })}
                        className="h-4 w-4 rounded border-white/25 bg-black/30"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function buildDrafts(stats: StatCatalogStat[]): Record<string, PersistentDraft> {
  const out: Record<string, PersistentDraft> = {};
  for (const s of stats) {
    const costs: Record<number, string> = {};
    for (const c of s.costs) costs[c.level] = String(c.coinCost);
    out[s.statId] = {
      description: s.description,
      enabled: s.enabled,
      costs,
    };
  }
  return out;
}

function persistentRowDiffers(entry: StatCatalogStat, draft: PersistentDraft): boolean {
  if (draft.description !== entry.description) return true;
  if (draft.enabled !== entry.enabled) return true;
  for (const c of entry.costs) {
    if (String(c.coinCost) !== draft.costs[c.level]) return true;
  }
  return false;
}

// ─── Weekly rules ────────────────────────────────────────────────────

interface RulesDraft {
  title: string;
  version: string;
  effectiveFromLocal: string; // datetime-local input value
  published: boolean;
}

function WeeklyRulesEditor() {
  const [rules, setRules] = useState<WeeklyRulesEntry[] | null>(null);
  const [drafts, setDrafts] = useState<Record<number, RulesDraft>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyWeek, setBusyWeek] = useState<number | null>(null);
  const [successWeek, setSuccessWeek] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAdminWeeklyRules({ signal: controller.signal })
      .then((d) => {
        setRules(d);
        setDrafts(buildRulesDrafts(d));
        setError(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(
          err instanceof ApiError
            ? `Couldn't load weekly rules (${err.status}).`
            : "Couldn't load weekly rules.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const patch = (weekNumber: number, p: Partial<RulesDraft>): void => {
    setDrafts((cur) => ({ ...cur, [weekNumber]: { ...cur[weekNumber]!, ...p } }));
    if (successWeek === weekNumber) setSuccessWeek(null);
  };

  const isDirty = (entry: WeeklyRulesEntry): boolean => {
    const d = drafts[entry.weekNumber];
    if (!d) return false;
    return (
      d.title !== entry.title ||
      d.version !== entry.version ||
      d.published !== entry.published ||
      d.effectiveFromLocal !== isoToLocalInput(entry.effectiveFrom)
    );
  };

  const reset = (entry: WeeklyRulesEntry): void => {
    setDrafts((cur) => ({ ...cur, [entry.weekNumber]: entryToDraft(entry) }));
    setError(null);
    setSuccessWeek(null);
  };

  const save = async (entry: WeeklyRulesEntry): Promise<void> => {
    const d = drafts[entry.weekNumber]!;
    if (!d.title.trim() || !d.version.trim()) {
      setError(`Week ${entry.weekNumber}: title and version are required.`);
      return;
    }
    const iso = d.effectiveFromLocal ? localInputToIso(d.effectiveFromLocal) : null;
    if (d.effectiveFromLocal && !iso) {
      setError(`Week ${entry.weekNumber}: effective-from is not a valid date.`);
      return;
    }

    setBusyWeek(entry.weekNumber);
    setError(null);
    setSuccessWeek(null);
    try {
      // Send only changed fields so an idle field can't accidentally overwrite
      // a concurrent edit by someone else.
      const update: Record<string, unknown> = {};
      if (d.title !== entry.title) update.title = d.title.trim();
      if (d.version !== entry.version) update.version = d.version.trim();
      if (d.published !== entry.published) update.published = d.published;
      if (iso && iso !== entry.effectiveFrom) update.effectiveFrom = iso;
      if (Object.keys(update).length === 0) {
        setBusyWeek(null);
        return;
      }

      const next = await updateAdminWeeklyRule(entry.weekNumber, update);
      setRules((cur) => cur?.map((r) => (r.weekNumber === next.weekNumber ? next : r)) ?? null);
      setDrafts((cur) => ({ ...cur, [next.weekNumber]: entryToDraft(next) }));
      setSuccessWeek(next.weekNumber);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Couldn't save week ${entry.weekNumber}.`);
    } finally {
      setBusyWeek(null);
    }
  };

  return (
    <section className="rounded-xl border border-white/15 bg-black/20 p-5">
      <header>
        <h2 className="font-display text-xl uppercase tracking-wide text-white">Weekly rules</h2>
        <p className="text-sm text-white/55">
          Publication metadata only; the rules body lives in code. Toggle <code>published</code> to flip the body
          on/off without a deploy.
        </p>
      </header>

      {error && <p className="mt-3 text-sm text-[#ff8da6]">{error}</p>}

      <div className={`mt-4 flex flex-col gap-4 ${loading ? 'opacity-60' : 'opacity-100'}`}>
        {loading && !rules ? (
          <p className="text-sm text-white/55">Loading rules…</p>
        ) : !rules || rules.length === 0 ? (
          <p className="text-sm text-white/55">No weekly rules configured.</p>
        ) : (
          rules.map((entry) => {
            const d = drafts[entry.weekNumber];
            if (!d) return null;
            const busy = busyWeek === entry.weekNumber;
            const dirty = isDirty(entry);
            const isSuccess = successWeek === entry.weekNumber;
            return (
              <div
                key={entry.weekNumber}
                className="rounded-lg border border-white/10 bg-black/30 p-4"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-heading text-sm uppercase tracking-wide text-white">
                    Week {entry.weekNumber}
                  </h3>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-white/55">
                    <span>slug: {entry.slug}</span>
                    {entry.modifiedAt && <span>Last edit: {fmtDateTime(entry.modifiedAt)}</span>}
                    <span
                      className={`rounded px-2 py-0.5 ${
                        entry.published
                          ? 'bg-[#0f3d2a] text-[#5bffb4]'
                          : 'bg-[#3d240f] text-[#ffae5b]'
                      }`}
                    >
                      {entry.published ? 'Published' : 'Placeholder'}
                    </span>
                  </div>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <Field label="Title">
                    <input
                      type="text"
                      value={d.title}
                      onChange={(e) => patch(entry.weekNumber, { title: e.target.value })}
                      maxLength={255}
                      className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
                    />
                  </Field>
                  <Field label="Version">
                    <input
                      type="text"
                      value={d.version}
                      onChange={(e) => patch(entry.weekNumber, { version: e.target.value })}
                      maxLength={32}
                      className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
                    />
                  </Field>
                  <Field label="Effective from (local time)">
                    <input
                      type="datetime-local"
                      value={d.effectiveFromLocal}
                      onChange={(e) => patch(entry.weekNumber, { effectiveFromLocal: e.target.value })}
                      className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
                    />
                  </Field>
                  <label className="flex items-center gap-2 text-sm text-white/85">
                    <input
                      type="checkbox"
                      checked={d.published}
                      onChange={(e) => patch(entry.weekNumber, { published: e.target.checked })}
                      className="h-4 w-4 rounded border-white/25 bg-black/30"
                    />
                    Published (renders the rules body on /rules)
                  </label>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => reset(entry)}
                    disabled={!dirty || busy}
                    className="rounded-full border border-white/25 px-4 py-1.5 text-xs uppercase tracking-wide text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Revert
                  </button>
                  <button
                    type="button"
                    onClick={() => void save(entry)}
                    disabled={!dirty || busy}
                    className="rounded-full bg-[#00ff8b] px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-[#10231a] transition hover:bg-[#5bffb4] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busy ? 'Saving…' : 'Save week'}
                  </button>
                  {isSuccess && <span className="text-xs text-[#5bffb4]">Saved.</span>}
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function buildRulesDrafts(rules: WeeklyRulesEntry[]): Record<number, RulesDraft> {
  const out: Record<number, RulesDraft> = {};
  for (const r of rules) out[r.weekNumber] = entryToDraft(r);
  return out;
}

function entryToDraft(r: WeeklyRulesEntry): RulesDraft {
  return {
    title: r.title,
    version: r.version,
    effectiveFromLocal: isoToLocalInput(r.effectiveFrom),
    published: r.published,
  };
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
      {label}
      {children}
    </label>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="px-3 py-2 font-heading text-xs uppercase tracking-wide">{children}</th>;
}

// ─── In-game picks ───────────────────────────────────────────────────

/**
 * Editable subset of an in-game pick row. Per the catalog spec only
 * description / sortOrder / theme / iconAsset / enabled are user-editable
 * from the admin tab — the other fields (statId, name, maxLevel,
 * relatedStatId) are stable enough that an inadvertent edit would mostly
 * cause confusion. Renaming a pick or rewiring its relatedStatId is a
 * deliberate enough change that it should go through a migration, not a
 * one-click typo.
 */
interface InGameDraft {
  description: string;
  sortOrder: string;
  theme: string;
  iconAsset: string;
  enabled: boolean;
}

const THEME_OPTIONS = ['cyan', 'orange', 'green'] as const;

/** Asset basenames shipped under jakes-jingle-quest/src/img/puMenu/. Resolved
 *  by the game build as `./src/img/puMenu/${iconAsset}.png`. Keep aligned with
 *  the puMenu/ folder; new icon names land here when re-vendoring adds them. */
const ICON_ASSET_OPTIONS = [
  'hazard-controll',
  'pi_backwardsShot',
  'pi_biggerShot',
  'pi_bombs',
  'pi_extraShot',
  'pi_fasterShot',
  'pi_fireballs',
  'pi_footSpeed',
  'pi_frost',
  'pi_heal',
  'pi_lightningStrike',
  'pi_magnet',
  'pi_ninjaStar',
  'pi_splinter',
  'umbrellaCoverage',
] as const;

function InGameStatsEditor() {
  const [stats, setStats] = useState<InGameStatEntry[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, InGameDraft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAdminInGameStats({ signal: controller.signal })
      .then((d) => {
        setStats(d);
        setDrafts(buildInGameDrafts(d));
        setMessage(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setMessage({
          kind: 'error',
          text:
            err instanceof ApiError
              ? `Couldn't load in-game picks (${err.status}).`
              : "Couldn't load in-game picks.",
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const dirty = useMemo(() => {
    if (!stats) return false;
    for (const s of stats) {
      const d = drafts[s.statId];
      if (!d) continue;
      if (rowDiffers(s, d)) return true;
    }
    return false;
  }, [stats, drafts]);

  const patch = (statId: string, p: Partial<InGameDraft>): void => {
    setDrafts((cur) => ({ ...cur, [statId]: { ...cur[statId]!, ...p } }));
  };

  const reset = (): void => {
    if (!stats) return;
    setDrafts(buildInGameDrafts(stats));
    setMessage(null);
  };

  const save = async (): Promise<void> => {
    if (!stats) return;
    const changed: InGameStatPatch[] = [];
    for (const s of stats) {
      const d = drafts[s.statId];
      if (!d) continue;
      if (!rowDiffers(s, d)) continue;

      // Per-row validation. Surface the first error and stop — the admin
      // can fix it without losing the rest of their edits.
      const sortOrder = Number(d.sortOrder);
      if (!Number.isInteger(sortOrder) || sortOrder < 0 || sortOrder > 10_000) {
        setMessage({ kind: 'error', text: `"${s.name}" sortOrder must be an integer 0-10000.` });
        return;
      }
      if (!d.description.trim()) {
        setMessage({ kind: 'error', text: `"${s.statId}" description is required.` });
        return;
      }
      if (!d.theme.trim()) {
        setMessage({ kind: 'error', text: `"${s.statId}" theme is required.` });
        return;
      }
      if (!d.iconAsset.trim()) {
        setMessage({ kind: 'error', text: `"${s.statId}" iconAsset is required.` });
        return;
      }

      const out: InGameStatPatch = { statId: s.statId };
      if (d.description !== s.description) out.description = d.description.trim();
      if (sortOrder !== s.sortOrder) out.sortOrder = sortOrder;
      if (d.theme !== s.theme) out.theme = d.theme.trim();
      if (d.iconAsset !== s.iconAsset) out.iconAsset = d.iconAsset.trim();
      if (d.enabled !== s.enabled) out.enabled = d.enabled;
      changed.push(out);
    }
    if (changed.length === 0) return;

    setSaving(true);
    setMessage(null);
    try {
      const updated = await updateAdminInGameStats({ stats: changed });
      setStats(updated);
      setDrafts(buildInGameDrafts(updated));
      setMessage({
        kind: 'success',
        text: `Saved ${changed.length} pick${changed.length === 1 ? '' : 's'}.`,
      });
    } catch (err) {
      setMessage({
        kind: 'error',
        text: err instanceof ApiError ? err.message : "Couldn't save in-game picks.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-white/15 bg-black/20 p-5">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="font-display text-xl uppercase tracking-wide text-white">
            In-game level-up picks
          </h2>
          <p className="text-sm text-white/55">
            Level-up choice catalog the game fetches on session start. Reference data only —
            never persisted per-user. Edit description, sort, theme, icon asset, and the on/off
            toggle here; statId, name, max level, and the persistent linkage are read-only.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={reset}
            disabled={!dirty || saving}
            className="rounded-full border border-white/25 px-4 py-2 text-xs uppercase tracking-wide text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || saving}
            className="rounded-full bg-[#00ff8b] px-4 py-2 text-xs font-medium uppercase tracking-wide text-[#10231a] transition hover:bg-[#5bffb4] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </header>

      {message && (
        <p className={`mt-3 text-sm ${message.kind === 'error' ? 'text-[#ff8da6]' : 'text-[#5bffb4]'}`}>
          {message.text}
        </p>
      )}

      <div className={`mt-4 overflow-x-auto ${loading ? 'opacity-60' : 'opacity-100'}`}>
        {loading && !stats ? (
          <p className="text-sm text-white/55">Loading in-game picks…</p>
        ) : !stats || stats.length === 0 ? (
          <p className="text-sm text-white/55">No in-game picks configured.</p>
        ) : (
          <table className="w-full min-w-[1100px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/55">
                <Th>Stat</Th>
                <Th>Description</Th>
                <Th>Max Lv</Th>
                <Th>Theme</Th>
                <Th>Icon Asset</Th>
                <Th>Sort</Th>
                <Th>Related</Th>
                <Th>On</Th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => {
                const d = drafts[s.statId];
                if (!d) return null;
                return (
                  <tr
                    key={s.statId}
                    className="border-b border-white/5 align-top last:border-0 text-white/85"
                  >
                    <td className="px-3 py-3">
                      <div className="font-medium text-white">{s.name}</div>
                      <div className="text-xs text-white/45 [overflow-wrap:anywhere]">
                        {s.statId}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={d.description}
                        onChange={(e) => patch(s.statId, { description: e.target.value })}
                        maxLength={255}
                        className="w-full rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-white focus:border-white/40 focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-3 text-white/70">{s.maxLevel}</td>
                    <td className="px-2 py-2">
                      <select
                        value={d.theme}
                        onChange={(e) => patch(s.statId, { theme: e.target.value })}
                        className="w-24 rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-white focus:border-white/40 focus:outline-none"
                      >
                        {THEME_OPTIONS.map((t) => (
                          <option key={t} value={t} className="bg-black">
                            {t}
                          </option>
                        ))}
                        {!THEME_OPTIONS.includes(d.theme as (typeof THEME_OPTIONS)[number]) && (
                          <option value={d.theme} className="bg-black">
                            {d.theme} (legacy)
                          </option>
                        )}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <select
                        value={d.iconAsset}
                        onChange={(e) => patch(s.statId, { iconAsset: e.target.value })}
                        className="w-44 rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-white focus:border-white/40 focus:outline-none"
                      >
                        {ICON_ASSET_OPTIONS.map((i) => (
                          <option key={i} value={i} className="bg-black">
                            {i}
                          </option>
                        ))}
                        {!ICON_ASSET_OPTIONS.includes(
                          d.iconAsset as (typeof ICON_ASSET_OPTIONS)[number],
                        ) && (
                          <option value={d.iconAsset} className="bg-black">
                            {d.iconAsset} (legacy)
                          </option>
                        )}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={10_000}
                        value={d.sortOrder}
                        onChange={(e) => patch(s.statId, { sortOrder: e.target.value })}
                        className="w-20 rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-white focus:border-white/40 focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-3 text-xs text-white/55 [overflow-wrap:anywhere]">
                      {s.relatedStatId ?? <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-2 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={d.enabled}
                        onChange={(e) => patch(s.statId, { enabled: e.target.checked })}
                        className="h-4 w-4 rounded border-white/25 bg-black/30"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function buildInGameDrafts(stats: InGameStatEntry[]): Record<string, InGameDraft> {
  const out: Record<string, InGameDraft> = {};
  for (const s of stats) {
    out[s.statId] = {
      description: s.description,
      sortOrder: String(s.sortOrder),
      theme: s.theme,
      iconAsset: s.iconAsset,
      enabled: s.enabled,
    };
  }
  return out;
}

function rowDiffers(entry: InGameStatEntry, draft: InGameDraft): boolean {
  return (
    draft.description !== entry.description ||
    draft.sortOrder !== String(entry.sortOrder) ||
    draft.theme !== entry.theme ||
    draft.iconAsset !== entry.iconAsset ||
    draft.enabled !== entry.enabled
  );
}
